// Is the Google Maps API actually usable yet?
//
// Split from googleMaps.ts, which owns the script tag and cannot be unit-tested without a
// DOM. This half is where the "google.maps.Map is not a constructor" bug lived — twice —
// so it is kept free of browser plumbing and fully covered.

type MapsNamespace = { importLibrary?: (name: string) => Promise<unknown>; Map?: unknown }

/** Libraries this app constructs from: Map/LatLngBounds, and Marker. */
const NEEDED = ['maps', 'marker'] as const

function currentMaps(): MapsNamespace | undefined {
  return (globalThis as { google?: { maps?: MapsNamespace } }).google?.maps
}

/**
 * Resolves once the constructors exist, not merely the namespace.
 *
 * Under `loading=async` the bootstrap defines `google.maps` with its enums and
 * `importLibrary`, then fetches the constructors separately — so script.onload is not the
 * finish line.
 */
export async function awaitLibraries(): Promise<void> {
  const boot = currentMaps()
  if (!boot) throw new Error('maps_missing_after_load')

  if (typeof boot.importLibrary === 'function') {
    const load = boot.importLibrary.bind(boot)
    await Promise.all(NEEDED.map((name) => load(name)))
  }

  // Re-read rather than reusing `boot`: the bootstrap swaps the namespace object out as
  // libraries arrive, so the reference captured a moment ago can still be missing Map
  // while window.google.maps has it. Checking the stale one rejected a perfectly good load.
  const ready = currentMaps()
  if (typeof ready?.Map !== 'function') throw new Error('maps_incomplete')
}
